import { useQueryClient } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import React, { useRef, useState } from 'react'

import { Button } from 'slab'
import {
  getGetAdminMappedPlansQueryKey,
  getGetAdminPlanMappingsDownloadTemplateUrl,
  getGetAdminUnmappedPlansQueryKey,
  postAdminPlanMappingsCreateMappings,
} from 'utils/web_api'
import type { AdminCsvUploadResult } from 'utils/web_api.schemas'

import DownloadTemplateDropdown from './DownloadTemplateDropdown'
import MappedPlansTable from './MappedPlansTable'
import UnmappedPlansTable from './UnmappedPlansTable'

type ViewType = 'unmapped' | 'mapped'

const PlanDescendantMappings: React.FC = () => {
  const queryClient = useQueryClient()
  const [activeView, setActiveView] = useState<ViewType>('unmapped')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<AdminCsvUploadResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    setUploadResult(null)

    try {
      // Create FormData for multipart/form-data request
      const formData = new FormData()
      formData.append('csv_file', file)

      // Call the API
      const response = await postAdminPlanMappingsCreateMappings({
        body: formData,
      })

      setUploadResult(response)

      // Invalidate both queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: getGetAdminMappedPlansQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetAdminUnmappedPlansQueryKey() })
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : (error && typeof error === 'object' && 'message' in error
            ? String(error.message)
            : String(error))
      setUploadResult({
        message: errorMessage || 'An error occurred',
        errors: {},
      })
    } finally {
      setIsUploading(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleDownloadTemplate = (templateType: 'empty' | 'populated') => {
    const url = getGetAdminPlanMappingsDownloadTemplateUrl({ template_type: templateType })
    window.open(url, '_blank')
  }

  return (
    <div className='p-6 space-y-6'>
      <div className='flex items-start justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900'>Plan Descendant Mappings</h1>
        </div>
        <div className='flex flex-col items-end gap-2'>
          <a
            href='https://metabase.thatch.com/question/3984-find-a-descendant-plan-tool'
            target='_blank'
            rel='noopener noreferrer'
            className='text-purple-600 hover:text-purple-800 underline text-sm flex items-center gap-1'
          >
            Find a descendant plan
            <ExternalLink size={14} />
          </a>
          <div className='flex gap-2'>
            <DownloadTemplateDropdown onDownloadTemplate={handleDownloadTemplate} />

            <input
              ref={fileInputRef}
              type='file'
              accept='.csv'
              onChange={handleFileSelect}
              className='hidden'
            />
            <Button
              onClick={handleUploadClick}
              disabled={isUploading}
              variant='secondary'
            >
              {isUploading ? 'Uploading...' : 'Upload Plan Mappings'}
            </Button>
          </div>
        </div>
      </div>

      {/* Upload Result */}
      {uploadResult && (
        <div
          className={`rounded-lg p-4 ${
            !uploadResult.errors
              ? 'bg-green-50 border border-green-200'
              : 'bg-yellow-50 border border-yellow-200'
          }`}
        >
          <div className='flex items-start justify-between'>
            <div className='flex-1'>
              <p
                className={`font-medium ${
                  !uploadResult.errors
                    ? 'text-green-800'
                    : 'text-yellow-800'
                }`}
              >
                {uploadResult.message}
              </p>

              {/* Error Details */}
              {uploadResult.errors && Object.keys(uploadResult.errors).length > 0 && (
                <div className='mt-4 space-y-4'>
                  {Object.entries(uploadResult.errors).map(([errorType, rows]) => {
                    // Type guard to check if rows is an array
                    if (!Array.isArray(rows)) return null

                    return (
                      <div key={errorType} className='bg-white rounded p-3 border border-yellow-300'>
                        <p className='font-semibold text-sm text-gray-900 mb-2'>
                          {errorType} ({rows.length} rows)
                        </p>
                        <div className='max-h-48 overflow-y-auto'>
                          <table className='min-w-full text-sm'>
                            <thead className='bg-gray-50'>
                              <tr>
                                <th className='px-2 py-1 text-left text-xs font-medium text-gray-500'>
                                  Row
                                </th>
                                <th className='px-2 py-1 text-left text-xs font-medium text-gray-500'>
                                  Ancestor Plan ID
                                </th>
                                <th className='px-2 py-1 text-left text-xs font-medium text-gray-500'>
                                  Descendant Plan ID
                                </th>
                                <th className='px-2 py-1 text-left text-xs font-medium text-gray-500'>
                                  Error Details
                                </th>
                              </tr>
                            </thead>
                            <tbody className='bg-white divide-y divide-gray-200'>
                              {rows.map((row: any, idx: number) => (
                                <tr key={idx}>
                                  <td className='px-2 py-1 text-gray-900'>{row.row_number ?? '-'}</td>
                                  <td className='px-2 py-1 text-gray-600 font-mono text-xs'>
                                    {row.ancestor_plan_id || '-'}
                                  </td>
                                  <td className='px-2 py-1 text-gray-600 font-mono text-xs'>
                                    {row.descendant_plan_id || '-'}
                                  </td>
                                  <td className='px-2 py-1 text-gray-600 font-mono text-xs'>
                                    {row.message || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            <button
              onClick={() => setUploadResult(null)}
              className='ml-4 text-gray-400 hover:text-gray-600'
              aria-label='Dismiss'
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className='flex items-center gap-2'>
        <Button
          variant={activeView === 'unmapped' ? 'primary' : 'tertiary'}
          onClick={() => setActiveView('unmapped')}
        >
          Unmapped Plans
        </Button>
        <Button
          variant={activeView === 'mapped' ? 'primary' : 'tertiary'}
          onClick={() => setActiveView('mapped')}
        >
          Mapped Plans
        </Button>
      </div>

      {/* Content */}
      <div className='space-y-6'>
        {activeView === 'unmapped' && <UnmappedPlansTable />}
        {activeView === 'mapped' && <MappedPlansTable />}
      </div>
    </div>
  )
}

export default PlanDescendantMappings
